import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, SubHeader } from 'vetrf-ui';
import { dictApi } from 'api/dict';
import { ObjectOperation, ObjectTypeId } from 'api/dict/params';
import { CheckDataLoading } from 'components/CheckDataLoading/CheckDataLoading';
import { RemoveModal } from 'components/RemoveModal';
import { Timeline } from 'components/Timeline/Timeline';
import { AddMotionModal } from 'components/registry/components/RequestMotion/components/AddMotionModal';
import { DocumentFields } from 'components/registry/components/RequestMotion/components/DocumentFields';
import { RequestMotionItem } from './components/RequestMotionItem';
import {
  FunctionPermissions,
  GmpDto,
  MovementHistoryItem,
  MovementRequest,
} from '../../../../../../gmpRegistry/api/params';

type RequestMotionProps = {
  id: string;
  history: MovementHistoryItem[];
  updateData: () => void;
  addMovement: (id: string, data: MovementRequest) => Promise<MovementHistoryItem>;
  removeMovement: (id: string, movementId: string) => Promise<void>;
  objectTypeId: ObjectTypeId;
  cardData: GmpDto;
  permissions: FunctionPermissions;
};

export const RequestMotion = ({
  id,
  history,
  updateData,
  addMovement,
  removeMovement,
  objectTypeId,
  cardData,
  permissions,
}: RequestMotionProps) => {
  const [functions, setFunctions] = useState<ObjectOperation[]>([]);
  const [removeItemId, setRemoveItemId] = useState<string>('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [error, setError] = useState('');
  const lastStatusDate = useMemo(
    () => (history?.length ? history[history.length - 1].statusDate : undefined),
    [history]
  );

  const fetchFunctions = async () => {
    const res = await dictApi.getObjectOperations({
      objectTypeId,
      withObjectStatusModel: true,
      currentObjectStatusId: cardData.status!.id,
    });
    setFunctions(res);
  };

  useEffect(() => {
    fetchFunctions();
  }, []);

  const closeAddModal = () => {
    setIsAddModalOpen(false);
  };

  const openAddModal = () => {
    setIsAddModalOpen(true);
  };

  const closeRemoveModal = () => {
    setRemoveItemId('');
    setIsRemoveModalOpen(false);
  };

  const openRemoveModal = (item: MovementHistoryItem) => {
    setRemoveItemId(item.id!);
    setIsRemoveModalOpen(true);
  };

  const onAddRequestMotion = async (data: MovementRequest) => {
    addMovement(id, data)
      .then(() => updateData())
      .catch((e) => setError(e.response.data.message))
      .finally(closeAddModal);
  };

  const onRemoveRequestMotion = async () => {
    removeMovement(id, removeItemId)
      .then(() => updateData())
      .catch((e) => setError(e.response.data.message))
      .finally(closeRemoveModal);
  };

  return (
    <>
      <SubHeader
        header="Движения заявки"
        icon="exchange"
        iconColor="orange"
        iconSize={100}
        level={4}
        toolbar={
          permissions.edit && (
            <Button color="info" size="minier" text="Добавить" icon="plus" onClick={openAddModal} />
          )
        }
      />
      <div className="mt-15">
        {error && (
          <Alert closable messageType="danger">
            {error}
          </Alert>
        )}

        <CheckDataLoading condition={!!history}>
          <Timeline
            dateField="statusDate"
            nameField="status.name"
            defaultNameField="status.name"
            subtitle="operation.name"
            data={history}
            onRemove={openRemoveModal}
            allowRemove={permissions.edit}
            onlyLastRemove
            render={(item) => <RequestMotionItem item={item} permissions={permissions} />}
          />
        </CheckDataLoading>

        {isAddModalOpen && (
          <AddMotionModal
            onClose={closeAddModal}
            operationOptions={functions}
            onSubmit={onAddRequestMotion}
            lastStatusDate={lastStatusDate}
            extraForm={<DocumentFields />}
          />
        )}

        {isRemoveModalOpen && (
          <RemoveModal
            modalContent="Вы уверены, что хотите удалить движение заявки?"
            onClose={closeRemoveModal}
            onRemove={onRemoveRequestMotion}
          />
        )}
      </div>
    </>
  );
};
